import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  Scale,
  Brain,
  Users,
  Wallet,
  Building2,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Check,
  Gavel,
  FileText,
  Search,
  Calendar,
  CircleDollarSign,
  Globe2,
  Languages,
  Activity,
  HeartHandshake,
  ChevronRight,
} from "lucide-react";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";
import { Section, GlassCard, Eyebrow, StatBlock } from "../components/Section";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.6 },
};

/* =========================================================================
   1. HERO
========================================================================= */
const Hero = () => (
  <section
    data-testid="hero-section"
    className="relative min-h-[92vh] flex items-center overflow-hidden pt-24 pb-24 bg-ink"
  >
    {/* Neural network background */}
    <div className="absolute inset-0">
      <NeuralNetworkBackground density={36} />
    </div>
    <ScaleWatermark />
    {/* Aurora glow */}
    <div className="turquoise-aura -top-40 -left-40 opacity-60" />
    <div className="gold-aura -bottom-40 right-0 opacity-50" />
    <div className="grain-overlay" />

    <div className="container relative z-10 mx-auto px-6 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-5xl"
      >
        <div className="aj-chip mb-8" data-testid="hero-chip">
          <Sparkles className="w-3 h-3" />
          AI Powered · Lawyer Verified · Donor Funded
        </div>

        <h1 className="font-playfair text-[2.6rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[1.02] text-white tracking-tight">
          Justice Should Not Be{" "}
          <span className="italic text-gold-gradient">a Privilege.</span>
          <br />
          It Should Be{" "}
          <span className="relative inline-block">
            <span className="text-aj-gradient">Accessible</span>
            <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 400 10" preserveAspectRatio="none">
              <path d="M0,5 Q200,0 400,5" stroke="#00C8D7" strokeWidth="1.5" fill="none" opacity="0.6" />
            </svg>
          </span>{" "}
          to Everyone.
        </h1>

        <p className="font-inter text-base md:text-lg lg:text-xl text-white/65 mt-8 max-w-3xl leading-relaxed">
          AccessJustice.Global is an AI-powered digital justice platform connecting people
          who need legal help with verified lawyers, intelligent legal guidance, and funded
          access to justice through the <span className="text-gold-200">Justice Wallet</span>.
        </p>

        <div className="flex flex-wrap items-center gap-4 mt-12">
          <Link to="/ai-assistant" data-testid="hero-cta-legal-help" className="btn-aj-primary">
            Get Legal Help Now <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/for-lawyers" data-testid="hero-cta-lawyer" className="btn-aj-secondary">
            Join as a Lawyer
          </Link>
          <Link to="/for-governments" data-testid="hero-cta-partner" className="btn-aj-ghost">
            Partner With Us
          </Link>
        </div>

        {/* Live signal row */}
        <div className="flex flex-wrap items-center gap-8 mt-16 pt-8 border-t border-white/8">
          <div className="flex items-center gap-2.5">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-turquoise opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-turquoise" />
            </span>
            <span className="font-montserrat text-[11px] uppercase tracking-[0.22em] text-white/70">
              Justice-AI Online
            </span>
          </div>
          <div className="font-montserrat text-[11px] uppercase tracking-[0.22em] text-white/40">
            5 Billion + Underserved
          </div>
          <div className="font-montserrat text-[11px] uppercase tracking-[0.22em] text-white/40">
            Multi-jurisdiction
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* =========================================================================
   2. TRUST BAR
========================================================================= */
const TrustBar = () => {
  const partners = [
    "Nigerian Bar Association",
    "Federal Ministry of Justice",
    "Office of the Deputy Speaker",
    "UNDP Engagement",
    "Development Partners",
    "Global Legal Innovation Stakeholders",
  ];
  return (
    <div className="relative py-14 border-y border-white/8 bg-black/40 backdrop-blur-sm" data-testid="trust-bar">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-8">
          <Eyebrow>Supported Through Strategic Collaboration With</Eyebrow>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-5">
          {partners.map((p, i) => (
            <motion.div
              key={p}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="font-montserrat text-[11px] sm:text-xs uppercase tracking-[0.22em] text-white/45 hover:text-gold-200 transition-colors duration-300 cursor-default"
            >
              {p}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* =========================================================================
   3. GLOBAL JUSTICE GAP
========================================================================= */
const JusticeGap = () => {
  const barriers = [
    { icon: CircleDollarSign, label: "High cost of legal representation" },
    { icon: Search, label: "Low legal awareness" },
    { icon: Globe2, label: "Distance from qualified lawyers" },
    { icon: FileText, label: "Complicated justice systems" },
    { icon: Brain, label: "Lack of trusted legal information" },
    { icon: HeartHandshake, label: "Limited funding for vulnerable citizens" },
  ];
  return (
    <Section
      testId="justice-gap"
      eyebrow="The Global Justice Gap"
      title={
        <>
          <span className="text-white">5 billion people lack</span>{" "}
          <span className="italic text-gold-gradient">meaningful access to justice.</span>
        </>
      }
      aurora
    >
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="flex flex-col gap-2 mb-8">
            <span className="font-playfair italic text-[6rem] md:text-[9rem] text-gold-gradient leading-none tracking-tight">
              5B+
            </span>
            <span className="font-montserrat text-xs uppercase tracking-[0.22em] text-white/55">
              People without access to justice
            </span>
          </div>
          <p className="font-inter text-base md:text-lg text-white/65 leading-relaxed">
            This is not because lawyers do not exist. It is because legal services remain
            <span className="text-white"> expensive</span>,
            <span className="text-white"> complex</span>,
            <span className="text-white"> slow</span>, and
            <span className="text-white"> inaccessible </span>
            to many who need them most.
          </p>
          <div className="mt-10 p-6 rounded-2xl border border-gold-400/30 bg-gold-400/[0.04]">
            <p className="font-cormorant italic text-xl md:text-2xl text-gold-100 leading-snug">
              &ldquo;AccessJustice.Global was built to close this gap using artificial intelligence,
              verified lawyers, and transparent digital justice infrastructure.&rdquo;
            </p>
          </div>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {barriers.map((b, i) => (
            <motion.div
              key={b.label}
              {...fadeUp}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              className="glass rounded-xl p-5 flex items-start gap-4 hover:border-turquoise/30 transition-all"
            >
              <div className="shrink-0 w-10 h-10 rounded-lg bg-turquoise/10 border border-turquoise/25 flex items-center justify-center">
                <b.icon className="w-4.5 h-4.5 text-turquoise" />
              </div>
              <div className="font-inter text-sm text-white/80 leading-relaxed pt-1.5">
                {b.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

/* =========================================================================
   4. INTRODUCING JUSTICE-AI
========================================================================= */
const IntroducingAI = () => {
  const features = [
    { icon: Brain, title: "AI-Powered Legal Guidance", desc: "Plain-language preliminary advice grounded in verified legal materials." },
    { icon: ShieldCheck, title: "Verified Lawyer Network", desc: "Bar-confirmed lawyers vetted for jurisdiction, expertise and ethics." },
    { icon: Users, title: "Location-Aware Matching", desc: "Uber-style engine matches your matter to the most qualified lawyer near you." },
    { icon: Calendar, title: "Secure Digital Consultation", desc: "Book, message and meet — virtual, phone or in person — all in one place." },
    { icon: Activity, title: "Case Tracking Dashboard", desc: "Live status, documents, payments and milestones for every legal matter." },
    { icon: Wallet, title: "Justice Wallet Funding", desc: "Donor-funded legal support for vulnerable users who cannot afford fees." },
  ];
  return (
    <Section
      testId="introducing-ai"
      eyebrow="Introducing Justice-AI"
      title={
        <>
          Not a chatbot.{" "}
          <span className="text-aj-gradient">The Operating System for Justice.</span>
        </>
      }
      subtitle="JUSTICE-AI is the intelligent engine powering AccessJustice.Global. It helps users understand their legal issues, receive preliminary guidance, and connect with the most suitable lawyer by location, expertise, availability and urgency."
    >
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.map((f, i) => (
          <motion.div
            key={f.title}
            {...fadeUp}
            transition={{ duration: 0.55, delay: i * 0.06 }}
          >
            <GlassCard glow className="h-full">
              <div className="w-11 h-11 rounded-xl bg-turquoise/10 border border-turquoise/25 flex items-center justify-center mb-5">
                <f.icon className="w-5 h-5 text-turquoise" />
              </div>
              <h3 className="font-playfair text-2xl text-white mb-3 leading-tight">
                {f.title}
              </h3>
              <p className="font-inter text-sm text-white/60 leading-relaxed">
                {f.desc}
              </p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

/* =========================================================================
   5. HOW IT WORKS — 3-track tabs
========================================================================= */
const HowItWorks = () => {
  const tracks = {
    citizen: {
      label: "For Citizens",
      icon: Users,
      steps: [
        { title: "Explain Your Legal Issue", desc: "Tell Justice-AI what happened in simple language." },
        { title: "Receive Initial Guidance", desc: "Get clear, jurisdiction-aware preliminary advice." },
        { title: "Match With a Lawyer", desc: "Get matched to a verified lawyer for your matter." },
        { title: "Book Consultation", desc: "Schedule online, by phone, or in person." },
        { title: "Track Your Case", desc: "Follow progress on your secure dashboard." },
      ],
    },
    lawyer: {
      label: "For Lawyers",
      icon: Gavel,
      steps: [
        { title: "Get Verified", desc: "Submit Bar credentials and practice areas." },
        { title: "Receive Client Requests", desc: "Real-time matched matters in your inbox." },
        { title: "Use the AI Copilot", desc: "Research, drafting, case strategy in seconds." },
        { title: "Manage Cases & Payments", desc: "Escrow-protected milestones inside the platform." },
        { title: "Build Reputation", desc: "Ratings power your visibility and referrals." },
      ],
    },
    donor: {
      label: "For Donors / NGOs",
      icon: Wallet,
      steps: [
        { title: "Fund a Justice Category", desc: "Sponsor women, youth, rural or rights cases." },
        { title: "Eligibility Assessment", desc: "AI vetting + admin approval per beneficiary." },
        { title: "Lawyer Assigned via Wallet", desc: "Funds released against milestone delivery." },
        { title: "Live Impact Reporting", desc: "Beneficiaries, demographics, SDG 16 metrics." },
        { title: "Annual Audit & Outcomes", desc: "Transparent fund utilization and case results." },
      ],
    },
  };
  const [active, setActive] = useState("citizen");
  const cur = tracks[active];

  return (
    <Section
      testId="how-it-works"
      eyebrow="How It Works"
      title={
        <>
          One platform.{" "}
          <span className="italic text-white/80">Three journeys.</span>{" "}
          <span className="text-aj-gradient">Zero friction.</span>
        </>
      }
      className="bg-white/[0.015]"
    >
      <div className="flex flex-wrap gap-3 mb-12">
        {Object.entries(tracks).map(([key, t]) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            data-testid={`hiw-tab-${key}`}
            className={`flex items-center gap-2 px-5 py-3 rounded-full font-montserrat text-xs uppercase tracking-[0.16em] border transition-all ${
              active === key
                ? "bg-turquoise text-ink border-turquoise shadow-[0_0_24px_rgba(0,200,215,0.35)]"
                : "bg-transparent text-white/70 border-white/15 hover:border-white/40 hover:text-white"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        {cur.steps.map((s, i) => (
          <motion.div
            key={`${active}-${i}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: i * 0.08 }}
            className="relative glass rounded-2xl p-6 hover:border-turquoise/30 hover:shadow-[0_0_24px_rgba(0,200,215,0.1)] transition-all"
          >
            <div className="font-playfair italic text-5xl text-gold-gradient leading-none mb-4">
              0{i + 1}
            </div>
            <h4 className="font-playfair text-lg text-white mb-2 leading-tight">{s.title}</h4>
            <p className="font-inter text-xs text-white/55 leading-relaxed">{s.desc}</p>
            {i < cur.steps.length - 1 && (
              <ChevronRight className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-turquoise/40" />
            )}
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

/* =========================================================================
   6. LAWYER DASHBOARD SHOWCASE
========================================================================= */
const LawyerDashboard = () => {
  const features = [
    "AI Legal Research Assistant",
    "Case Analysis & Strategy",
    "Client Matching Engine",
    "Document Drafting Studio",
    "Escrow Payment Dashboard",
    "Calendar & Booking",
    "Justice Wallet Opportunities",
    "Ratings & Performance",
  ];
  return (
    <Section
      testId="lawyer-dashboard"
      eyebrow="Lawyer Operating System"
      title={
        <>
          Built for the{" "}
          <span className="italic text-aj-gradient">future of legal practice.</span>
        </>
      }
      subtitle="AccessJustice.Global empowers lawyers with AI productivity tools, verified clients, escrow-protected payments and a dashboard built for serious legal work."
    >
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        {/* Mock dashboard window */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -inset-6 bg-turquoise/10 blur-3xl rounded-3xl" />
            <div className="relative glass-strong rounded-2xl p-1.5">
              {/* window chrome */}
              <div className="flex items-center gap-2 px-4 py-2 border-b border-white/8">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                <div className="ml-3 font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/45">
                  justice-os · barr.adekunle@accessjusticeglobal.org
                </div>
              </div>
              <div className="grid grid-cols-12 gap-3 p-4">
                {/* sidebar */}
                <div className="col-span-3 space-y-1.5">
                  {["Inbox", "Cases", "Drafts", "Calendar", "Wallet", "Research", "Ratings"].map((s, i) => (
                    <div
                      key={s}
                      className={`px-3 py-1.5 rounded-md font-montserrat text-[11px] tracking-wider uppercase ${
                        i === 1
                          ? "bg-turquoise/15 text-turquoise border border-turquoise/30"
                          : "text-white/55 border border-transparent"
                      }`}
                    >
                      {s}
                    </div>
                  ))}
                </div>
                {/* main panel */}
                <div className="col-span-9 space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { k: "Active Cases", v: "14" },
                      { k: "Pending Reviews", v: "3" },
                      { k: "Wallet Cases", v: "5" },
                    ].map((m) => (
                      <div key={m.k} className="rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2.5">
                        <div className="font-montserrat text-[9px] uppercase tracking-[0.22em] text-white/45">{m.k}</div>
                        <div className="font-playfair text-2xl text-white mt-0.5">{m.v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-gold-200">New Client Request</div>
                      <div className="aj-chip aj-chip-gold !text-[9px] !py-1">High Urgency</div>
                    </div>
                    <div className="font-playfair text-lg text-white mb-1">Family Law · Domestic Violence</div>
                    <div className="font-inter text-xs text-white/50 mb-3">Abuja · Match score 94% · Justice Wallet eligible</div>
                    <div className="flex gap-2">
                      <span className="px-3 py-1.5 rounded-md bg-turquoise text-ink font-montserrat text-[10px] uppercase tracking-wider font-semibold">Accept</span>
                      <span className="px-3 py-1.5 rounded-md border border-white/20 text-white/70 font-montserrat text-[10px] uppercase tracking-wider">Decline</span>
                    </div>
                  </div>
                  <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3">
                    <div className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-turquoise mb-2">AI Copilot</div>
                    <div className="font-inter text-xs text-white/70 leading-relaxed">
                      Drafted: <span className="text-white">Petition for divorce (DV-grounded)</span> · 8 authorities cited · ready for review.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="space-y-3 mb-10">
            {features.map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-center gap-3"
              >
                <div className="w-5 h-5 rounded-full bg-turquoise/15 border border-turquoise/40 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-turquoise" />
                </div>
                <span className="font-inter text-sm text-white/80">{f}</span>
              </motion.div>
            ))}
          </div>
          <Link to="/for-lawyers" data-testid="lawyer-dashboard-cta" className="btn-aj-primary">
            Become a Justice-AI Lawyer <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </Section>
  );
};

/* =========================================================================
   7. JUSTICE WALLET
========================================================================= */
const JusticeWallet = () => {
  const supportedGroups = [
    "Women and vulnerable groups",
    "Low-income citizens",
    "Youth",
    "Rural communities",
    "Human rights matters",
    "Public-interest cases",
  ];
  const donorBenefits = [
    { icon: Activity, t: "Transparent Fund Tracking" },
    { icon: ShieldCheck, t: "Verified Lawyer Payments" },
    { icon: FileText, t: "Case-Level Reporting" },
    { icon: Globe2, t: "SDG 16 Alignment" },
  ];

  return (
    <Section testId="justice-wallet" className="relative">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <Eyebrow>Justice Wallet</Eyebrow>
          <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-white mt-4 leading-[1.05]">
            Funding justice for those who{" "}
            <span className="italic text-gold-gradient">cannot afford it.</span>
          </h2>
          <p className="font-inter text-base md:text-lg text-white/65 mt-6 leading-relaxed max-w-xl">
            The Justice Wallet is a transparent funding mechanism that allows donors,
            development agencies, NGOs, foundations and corporate partners to sponsor
            legal support for underserved people.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 gap-3">
            {supportedGroups.map((g) => (
              <div key={g} className="flex items-center gap-3 text-white/75">
                <div className="w-1.5 h-1.5 bg-gold-300 rounded-full" />
                <span className="font-inter text-sm">{g}</span>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/for-ngos" data-testid="wallet-cta" className="btn-aj-secondary">
              Support the Justice Wallet <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Donor visual */}
        <div className="lg:col-span-6">
          <div className="relative">
            <div className="absolute -inset-10 bg-gold-400/10 blur-3xl rounded-full" />
            <div className="relative glass-strong rounded-3xl p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-gold-200">Donor Dashboard</div>
                  <div className="font-playfair text-2xl text-white mt-1">Impact at a glance</div>
                </div>
                <Wallet className="w-7 h-7 text-gold-300" />
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                  <div className="font-playfair italic text-3xl md:text-4xl text-gold-gradient">₦12.4M</div>
                  <div className="font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Funds Disbursed</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                  <div className="font-playfair italic text-3xl md:text-4xl text-aj-gradient">387</div>
                  <div className="font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Cases Funded</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                  <div className="font-playfair italic text-3xl md:text-4xl text-white">62%</div>
                  <div className="font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Women Beneficiaries</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                  <div className="font-playfair italic text-3xl md:text-4xl text-white">SDG 16</div>
                  <div className="font-montserrat text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Aligned</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {donorBenefits.map((b) => (
                  <div key={b.t} className="flex items-center gap-2.5 rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2.5">
                    <b.icon className="w-4 h-4 text-turquoise" />
                    <span className="font-inter text-xs text-white/75">{b.t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

/* =========================================================================
   8. INSTITUTIONAL BENEFITS
========================================================================= */
const Institutional = () => {
  const groups = [
    {
      title: "Governments",
      icon: Building2,
      points: ["Expand citizen legal aid", "Track service delivery", "Justice impact data", "Rule-of-law strengthening"],
    },
    {
      title: "NGOs & Foundations",
      icon: HeartHandshake,
      points: ["Vulnerable group programs", "Donor-grade reporting", "Beneficiary verification", "Outcome accountability"],
    },
    {
      title: "Development Partners",
      icon: Globe2,
      points: ["SDG 16 alignment", "Cross-border justice infra", "Capacity-building data", "AI for Good deployment"],
    },
  ];
  return (
    <Section
      testId="institutional"
      eyebrow="Institutional Value"
      title={
        <>
          Built for governments, bar associations,{" "}
          <span className="italic text-aj-gradient">NGOs and development partners.</span>
        </>
      }
      subtitle="AccessJustice.Global is more than a legal platform. It is digital justice infrastructure."
    >
      <div className="grid md:grid-cols-3 gap-6">
        {groups.map((g, i) => (
          <motion.div key={g.title} {...fadeUp} transition={{ duration: 0.55, delay: i * 0.1 }}>
            <GlassCard glow className="h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-gold-400/10 border border-gold-400/30 flex items-center justify-center">
                  <g.icon className="w-5 h-5 text-gold-300" />
                </div>
                <h3 className="font-playfair text-2xl text-white">{g.title}</h3>
              </div>
              <ul className="space-y-3">
                {g.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5">
                    <div className="w-1.5 h-1.5 bg-turquoise rounded-full mt-2 shrink-0" />
                    <span className="font-inter text-sm text-white/70">{p}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          </motion.div>
        ))}
      </div>
      <div className="mt-12">
        <Link to="/for-governments" data-testid="institutional-cta" className="btn-aj-secondary">
          Partner With AccessJustice.Global <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  );
};

/* =========================================================================
   9. WHY DIFFERENT — bento grid
========================================================================= */
const WhyDifferent = () => {
  const items = [
    { t: "AI Legal Triage", d: "Plain-language intake mapped to jurisdiction-specific law.", big: true, icon: Brain },
    { t: "Lawyer Verification", d: "Bar-confirmed, vetted, rated.", icon: ShieldCheck },
    { t: "Professional Standards", d: "NBA-grade ethics oversight.", icon: Scale },
    { t: "Transparent Payments", d: "Escrow + milestone release.", icon: CircleDollarSign },
    { t: "Case Tracking", d: "Live status from intake to judgment.", icon: Activity },
    { t: "Donor-Funded Justice", d: "Wallet sponsors who cannot afford.", icon: Wallet },
    { t: "Institutional Collaboration", d: "Governments, NGOs, partners onboard.", icon: Building2 },
  ];
  return (
    <Section
      testId="why-different"
      eyebrow="Why AccessJustice Is Different"
      title={
        <>
          The Uber of Justice{" "}
          <span className="italic text-gold-gradient">— with ethics, trust and oversight.</span>
        </>
      }
      aurora
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((it, i) => (
          <motion.div
            key={it.t}
            {...fadeUp}
            transition={{ duration: 0.5, delay: i * 0.06 }}
            className={it.big ? "sm:col-span-2 lg:row-span-2" : ""}
          >
            <GlassCard glow className={`h-full ${it.big ? "lg:p-10" : ""}`}>
              <it.icon className={`text-turquoise mb-5 ${it.big ? "w-9 h-9" : "w-6 h-6"}`} />
              <h3 className={`font-playfair text-white leading-tight ${it.big ? "text-3xl md:text-4xl" : "text-xl"}`}>
                {it.t}
              </h3>
              <p className={`font-inter text-white/60 mt-3 leading-relaxed ${it.big ? "text-base" : "text-sm"}`}>
                {it.d}
              </p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

/* =========================================================================
   10. IMPACT VISION
========================================================================= */
const ImpactVision = () => {
  const targets = [
    { value: "Millions", label: "Users Served" },
    { value: "Thousands", label: "Lawyers Onboarded" },
    { value: "West Africa", label: "Phase 1 Expansion" },
    { value: "Global", label: "Phase 3 Reach" },
  ];
  return (
    <Section testId="impact-vision" className="border-t border-white/8">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Eyebrow>Impact Vision</Eyebrow>
          <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-white mt-4 leading-[1.05]">
            Our 4-year mission to{" "}
            <span className="italic text-aj-gradient">close the justice gap.</span>
          </h2>
          <p className="font-inter text-base md:text-lg text-white/65 mt-6 leading-relaxed">
            To reduce the global access-to-justice gap by making legal support accessible,
            affordable, intelligent and trusted — beginning in Nigeria and expanding across
            West Africa and the world.
          </p>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {targets.map((t, i) => (
            <motion.div
              key={t.label}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="glass rounded-2xl p-7 hover:border-gold-400/40 hover:shadow-[0_0_24px_rgba(184,134,11,0.1)] transition-all"
            >
              <StatBlock value={t.value} label={t.label} />
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

/* =========================================================================
   11. FINAL CTA
========================================================================= */
const FinalCTA = () => (
  <section className="relative overflow-hidden py-32" data-testid="final-cta">
    <div className="turquoise-aura top-0 left-1/2 -translate-x-1/2 opacity-70" />
    <div className="grain-overlay" />
    <div className="container relative z-10 mx-auto px-6 max-w-5xl text-center">
      <motion.div {...fadeUp}>
        <Eyebrow>Justice Begins With Access</Eyebrow>
        <h2 className="font-playfair text-4xl md:text-6xl lg:text-7xl text-white mt-5 leading-[1.05]">
          Whether you need legal help, want to grow your practice, or wish to fund justice for others —
          <br className="hidden md:block" />
          <span className="italic text-aj-gradient">we give you a trusted path forward.</span>
        </h2>
        <div className="flex flex-wrap justify-center gap-4 mt-12">
          <Link to="/ai-assistant" data-testid="final-cta-help" className="btn-aj-primary">
            Get Legal Help
          </Link>
          <Link to="/for-lawyers" data-testid="final-cta-lawyer" className="btn-aj-secondary">
            Join as a Lawyer
          </Link>
          <Link to="/for-ngos" data-testid="final-cta-donate" className="btn-aj-secondary">
            Donate to Justice Wallet
          </Link>
          <Link to="/for-governments" data-testid="final-cta-partner" className="btn-aj-ghost">
            Partner With Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

/* =========================================================================
   EXPORT
========================================================================= */
const Home = () => (
  <div className="bg-ink text-white">
    <Hero />
    <TrustBar />
    <JusticeGap />
    <IntroducingAI />
    <HowItWorks />
    <LawyerDashboard />
    <JusticeWallet />
    <Institutional />
    <WhyDifferent />
    <ImpactVision />
    <FinalCTA />
  </div>
);

export default Home;
